import { Button, CopyOnClick, Dialog, Div, H3, P } from '@dnb/eufemia';
import { people_1 as TeamIcon, person as UserIcon } from '@dnb/eufemia/icons';
import { BAID_MATCHER, BSNID_MATCHER } from '@portals/shared/common/matchers';
import { ApiType, ApiWithStatusAndScopesDto } from '@portals/shared/portal/ApiDto';
import classnames from 'classnames';
import { type JSX, useMemo } from 'react';

import Card from '@/components/Card';
import Dot from '@/components/Dot';
import { TeamOverview } from '@/components/TeamOverview/TeamOverview';
import { ApiClientResponse, AppWithApiKeyAccessResponse, AppWithApiOAuthAccessResponse } from '@/openapi/schema';
import { isEmptyString } from '@/utils/textUtils';

import { ViewType } from '../schemas';
import { AccessGrid } from './AccessGrid';
import { ApiKeyAccess } from './ApiKeyAccess';
import { ScopeAccess } from './ScopeAccess';

import style from './index.module.css';

type AppTileProps = {
  app: AppWithApiKeyAccessResponse | AppWithApiOAuthAccessResponse;
  clients?: ApiClientResponse[];
  showView: (view: ViewType, query?: string) => void;
  isLoading: boolean;
  api: ApiWithStatusAndScopesDto;
};

export default function AppTile({ app, clients, showView, isLoading, api }: AppTileProps): JSX.Element {
  const hasLiveAccess = useMemo(() => {
    if (app.type === 'API_KEY') {
      return app.api.some((api) => api.environment === 'LIVE');
    } else {
      return app.scopes.some((scope) => scope.environment === 'LIVE');
    }
  }, [app]);

  const hasDescription = !isEmptyString(app.description);
  const hasScopes = 'scopes' in app;
  const serviceNowIdentifier = app.serviceNowIdentifier;

  const serviceNowIdentifierLabel = useMemo(() => {
    if (!serviceNowIdentifier) return 'ServiceNow ID';
    if (BAID_MATCHER.test(serviceNowIdentifier)) return 'Business Application';
    if (BSNID_MATCHER.test(serviceNowIdentifier)) return 'Service Offering';
    return 'ServiceNow ID';
  }, [serviceNowIdentifier]);

  return (
    <Card className={style['AppTile']} direction="horizontal">
      <Div className={style['Row']}>
        {hasLiveAccess && <Dot right="small" />}
        <Div className={style['VerticalDivider']} left={!hasLiveAccess && 'large'} right="small" />
        <Div className={style['AppContent']}>
          <H3 bottom="small">{app.name}</H3>

          <div className={style['InformationGrid']}>
            <InformationLabel text="Owner" />
            <div>
              {app.owner.type === 'TEAM' ? (
                <Dialog
                  maxWidth="60rem"
                  title={app.owner.name}
                  triggerAttributes={{
                    tooltip: 'Click to view team details',
                    size: 'small',
                    variant: 'tertiary',
                    text: app.owner.name,
                    icon: TeamIcon,
                  }}
                >
                  <TeamOverview apiSlug={api.slug} teamId={app.owner.id} />
                </Dialog>
              ) : (
                <div>
                  <UserIcon />
                  <CopyOnClick className="text-basis" left="x-small">
                    {app.owner.email}
                  </CopyOnClick>
                </div>
              )}
            </div>

            <InformationLabel text="App ID" />
            <P>{app.id}</P>

            <InformationLabel text={serviceNowIdentifierLabel} />
            <P className={classnames({ NoData: !serviceNowIdentifier })}>{serviceNowIdentifier || 'Not provided'}</P>

            <InformationLabel text="Description" />
            <P className={classnames({ NoData: !hasDescription })}>
              {hasDescription ? app.description : 'No description'}
            </P>

            <InformationLabel text="Access" />
            {hasScopes ? <ScopeAccess scopes={app.scopes} /> : <ApiKeyAccess hasLiveAccess={hasLiveAccess} />}

            {hasScopes && (
              <>
                <InformationLabel text="Clients" />
                {clients && clients.length > 0 ? (
                  <AccessGrid
                    rows={clients?.map((client) => ({
                      label: (
                        <div>
                          <Button
                            onClick={() => showView('clients', client.ciamClientId)}
                            size="small"
                            tooltip="Show client details"
                            variant="tertiary"
                          >
                            {client.name}
                          </Button>
                        </div>
                      ),
                      hasTestAccess: !client.liveMode,
                      hasLiveAccess: client.liveMode,
                    }))}
                  />
                ) : (
                  <P
                    className={classnames({
                      NoData: !clients || clients.length === 0,
                    })}
                  >
                    {isLoading
                      ? 'Loading...'
                      : `No clients consuming this ${api.type == ApiType.EVENT ? 'event' : 'API'}`}
                  </P>
                )}
              </>
            )}
          </div>
        </Div>
      </Div>
    </Card>
  );
}

function InformationLabel({ text }: { text: string }): JSX.Element {
  return <P weight="medium">{text}:</P>;
}
